import { useRef, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { ReplyAttachments } from '@/components/reply-attachments'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Send,
  Check,
  CheckCircle2,
  MessageSquare,
  AlertCircle,
  ChevronRight,
  ChevronsUpDown,
  Phone,
  Receipt,
  RotateCcw,
} from 'lucide-react'
import {
  useRetryMessage,
  useSendReply,
  useRecordCommunication,
  useCloseThread,
  useReopenThread,
} from '@/features/communications/hooks'
import { useCurrentUser, useStaff } from '@/api/hooks'
import {
  MentionInput,
  MentionText,
  type MentionInputHandle,
  type MentionResult,
} from '@/components/comments'
import { mentionContentToPlainText } from '@/lib/mentions'
import { formatApiErrorMessage, getApiErrorMessage } from '@/lib/api-error'
import { cn, formatEventTime, parseUTCDate } from '@/lib/utils'
import type {
  CommunicationMessage,
  CommunicationThread,
  MessageAnalysis,
  MessageDraftUpdate,
  POCommunicationThread,
  ReplyAttachmentDraft,
  ShipmentDocOption,
} from '@/features/communications/types'
import { ThreadMessage } from '@/features/communications/message'
import { ThreadFollowUp } from '@/features/communications/follow-up'
import { ActionPopover } from '@/components/action-popover'
import { ThreadAnalysisPill } from '@/features/communications/analysis-status'
import {
  highlight,
  deliveryReport,
  isAiMessage,
  messageMatches,
  messagePreview,
} from '@/features/communications/thread-display'
import {
  ACCOUNTING_SIGNOFF,
  PURCHASING_SIGNOFF,
  parseCcString,
  personalSignoff,
  splitEmails,
} from './formatting'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { useActorName } from '@/hooks/use-actor-name'
import { type MessageActionLink } from '@/features/communications/message-actions'
import { useActionEmail } from '@/features/communications/action-email'
import { ACTION_FOCUS_CLASS, messageTargetProps } from '@/hooks/use-action-focus'
import {
  actionAuditEvents,
  actionResolvingMessageId,
  type ActionItemLike,
} from '@/features/purchase-orders/action-audit-model'
import {
  EditRecipientsDialog,
  RecipientSummary,
  type StaffSuggestion,
  StaffEmailField,
} from '@/features/communications/recipients'
import { useThreadMailbox } from '@/features/communications/mailbox'
import type { CallTarget } from '@/features/phone-calls/hooks'
import { RecentCallPicker } from '@/features/phone-calls/recent-call-picker'
import { ComposeTextarea } from '@/components/compose-field'

/** Threads longer than this fold their middle; the latest few stay in view. */
const FOLD_THRESHOLD = 6
const FOLD_KEEP_LATEST = 3

/** How a reply is signed: the mailbox's sign-off, the sender's own (USA-1557), or none. */
type SignAs = 'team' | 'personal' | 'none'
/** The sign-off a staff member picked last, so the next reply starts with it. */
const SIGN_AS_STORAGE_KEY = 'usars:reply-sign-as'

function rememberedSignAs(): SignAs {
  try {
    return localStorage.getItem(SIGN_AS_STORAGE_KEY) === 'personal' ? 'personal' : 'team'
  } catch {
    return 'team'
  }
}

function rememberSignAs(signAs: SignAs) {
  if (signAs === 'none') return
  try {
    localStorage.setItem(SIGN_AS_STORAGE_KEY, signAs)
  } catch {
    // Private browsing: the choice just isn't remembered.
  }
}

/** The AP invoice an accounting-mailbox thread belongs to. */
export interface ThreadInvoice {
  uuid: string
  number: string
}

/**
 * How a non-PO conversation (a Quote Builder quote or invoice, USA-1094) differs from a PO thread:
 * the greeting and sign-off around a reply, recording a call, and the entity it belongs to.
 */
export interface ThreadContextOptions {
  /** Shown before the subject, e.g. a link to the invoice. */
  badge?: ReactNode
  /** Reply greeting; null for none. Default "Dear Team,". */
  greeting?: string | null
  signoff?: string
  /** Offer signing with the sender's own name, title and phone (USA-1557). Default: true. */
  personalSignoff?: boolean
  /** Offer the PO, quote and shipment documents as attachments. Default: PO threads only. */
  poDocuments?: boolean
  onRecord?: (bodyText: string, source: string) => void
  /** Offer recording a call on this thread. Default: true (an invoice thread needs its own onRecord). */
  canRecord?: boolean
  isRecording?: boolean
  recordPlaceholder?: string
  /**
   * A page asking to write to the other side (USA-1504: a tax form's "Email customer"): each new `id`
   * opens the reply box with `text` in it.
   */
  composeRequest?: { id: number; text: string }
}

/** A PO thread as the PO page loads it; other conversations carry none of the PO-only fields. */
type ThreadWithSchedule = CommunicationThread &
  Partial<Pick<POCommunicationThread, 'analysis' | 'follow_up_reason' | 'upcoming_check_ins'>>

function ThreadView({
  thread,
  invoice,
  context,
  analysis,
  actions,
  hasPoPdf,
  hasQuotePdf,
  shipmentDocOptions,
  onApprove,
  onReject,
  onSend,
  onUpdate,
  onRetry,
  retryingUuid,
  onReply,
  isReplying,
  onRecord,
  isRecording,
  onClose,
  onReopen,
  isClosing,
  isReopening,
  open,
  onOpenChange,
  collapsible,
  query,
}: {
  thread: ThreadWithSchedule
  invoice?: ThreadInvoice
  context?: ThreadContextOptions
  analysis: MessageAnalysis[]
  actions: ActionItemLike[]
  hasPoPdf: boolean
  hasQuotePdf: boolean
  shipmentDocOptions: ShipmentDocOption[]
  onApprove: (uuid: string) => void
  onReject: (uuid: string) => void
  onSend: (uuid: string) => void
  onUpdate: (uuid: string, body: MessageDraftUpdate) => void
  onRetry: (uuid: string) => void
  retryingUuid: string | null
  onReply: (
    bodyText: string,
    opts: {
      attachPoPdf: boolean
      attachQuotePdf: boolean
      toEmails: string[]
      ccEmails: string[]
      attachShipmentDocs: Array<{ shipment_id: number; doc_type: 'bol' | 'label' }>
      attachments: ReplyAttachmentDraft[]
    },
    callbacks: { onSuccess: () => void; onError: (message: string) => void },
  ) => void
  isReplying: boolean
  onRecord: (bodyText: string, source: string) => void
  isRecording: boolean
  /** Close the thread; ``onDone`` runs once it is closed. */
  onClose: (onDone: () => void) => void
  onReopen: () => void
  isClosing: boolean
  isReopening: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  collapsible: boolean
  query: string
}) {
  const isClosed = thread.status === 'resolved' || thread.status === 'archived'
  const [ownExpanded, setOwnExpanded] = useState(!isClosed)
  const expanded = !collapsible || (open ?? ownExpanded)
  const setExpanded = (next: boolean) => (onOpenChange ? onOpenChange(next) : setOwnExpanded(next))
  // Unfolding applies to the search it was clicked under; a new search folds again.
  const [showAllFor, setShowAllFor] = useState<string | null>(null)
  const showAll = showAllFor === query
  const [messageOverrides, setMessageOverrides] = useState<{
    query: string
    open: Map<number, boolean>
  }>({ query: '', open: new Map() })
  const [composing, setComposing] = useState(false)
  const [composeText, setComposeText] = useState('')
  const composeInputRef = useRef<MentionInputHandle>(null)
  // Text a page asked to start the reply with; the reply box mounts with it once.
  const [composeSeed, setComposeSeed] = useState<{ id: number; text: string } | null>(null)
  const [seenComposeRequest, setSeenComposeRequest] = useState<number | null>(null)
  const [composeTo, setComposeTo] = useState(thread.recipient_email ?? '')
  const [composeCc, setComposeCc] = useState(() => parseCcString(thread.cc_emails))
  const [editingReplyRecipients, setEditingReplyRecipients] = useState(false)
  const composeRequest = context?.composeRequest
  if (composeRequest && composeRequest.id !== seenComposeRequest) {
    setSeenComposeRequest(composeRequest.id)
    setComposeTo(thread.recipient_email ?? '')
    setComposeCc(parseCcString(thread.cc_emails))
    setEditingReplyRecipients(false)
    setComposeText(composeRequest.text)
    setComposeSeed(composeRequest)
    setComposing(true)
  }
  const [draftComposeTo, setDraftComposeTo] = useState('')
  const [draftComposeCc, setDraftComposeCc] = useState('')
  const [attachPoPdf, setAttachPoPdf] = useState(false)
  const [attachQuotePdf, setAttachQuotePdf] = useState(false)
  /** Selected shipment doc keys, encoded as "{shipment_id}:{doc_type}" so we
   * can keep the set membership check cheap and round-trip cleanly. */
  const [selectedShipmentDocs, setSelectedShipmentDocs] = useState<Set<string>>(new Set())
  // Customer emails always sign as the company (USA-1353).
  const isCustomerThread = useThreadMailbox() === 'customer'
  const offersPersonal = !isCustomerThread && context?.personalSignoff !== false
  const currentUser = useCurrentUser().data
  const defaultSignAs = (): SignAs => (offersPersonal ? rememberedSignAs() : 'team')
  const [signAs, setSignAs] = useState<SignAs>(defaultSignAs)
  const includeSignoff = signAs !== 'none'
  // Invoice conversations go out from accounting@, so they sign as Accounting.
  const teamSignoff = context?.signoff ?? (invoice ? ACCOUNTING_SIGNOFF : PURCHASING_SIGNOFF)
  const signsPersonally = signAs === 'personal' && !!currentUser
  const signoff = signsPersonally ? personalSignoff(currentUser) : teamSignoff
  // A personal reply opens however its sender types it, e.g. "Hi Tim," (USA-1557).
  const greeting =
    signsPersonally ? null : context?.greeting === undefined ? 'Dear Team,' : context.greeting
  const poDocuments = context?.poDocuments ?? !invoice
  const [composeAttachments, setComposeAttachments] = useState<ReplyAttachmentDraft[]>([])
  const [sendError, setSendError] = useState<string | null>(null)
  const [attaching, setAttaching] = useState(false)
  const [recording, setRecording] = useState(false)
  const [callPicked, setCallPicked] = useState(false)
  // Record Communication offers this record's recent Quo calls to link (USA-217).
  const callTarget: CallTarget | null =
    isCustomerThread
      ? { customer_thread_id: thread.id }
      : thread.entity_type === 'ap_invoice'
        ? { ap_invoice_id: thread.entity_id }
        : thread.purchase_order_id
          ? { purchase_order_id: thread.purchase_order_id }
          : null
  const [recordText, setRecordText] = useState('')
  const [recordSource, setRecordSource] = useState('phone_call')
  const [editRecipientsOpen, setEditRecipientsOpen] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  const staffQuery = useStaff()
  const ccList: string[] = (() => {
    if (!thread.cc_emails) return []
    try {
      const parsed: unknown = JSON.parse(thread.cc_emails)
      return Array.isArray(parsed) ? parsed.filter((e): e is string => typeof e === 'string') : []
    } catch {
      return []
    }
  })()
  const allMessages = [...(thread.messages || [])].sort(
    (a, b) => parseUTCDate(a.created_at).getTime() - parseUTCDate(b.created_at).getTime(),
  )
  const staffSuggestions: StaffSuggestion[] = (staffQuery.data ?? [])
    .filter((user) => !!user.email)
    .map((user) => ({ name: user.name ?? null, email: user.email }))

  // Separate real emails from AI analysis messages, and pair AI analyses
  // with the inbound email they follow
  const emailMessages = allMessages.filter((m) => !isAiMessage(m))
  const aiMessages = allMessages.filter(isAiMessage)

  // Map each inbound email id → the AI analysis that follows it
  const aiByInboundId = new Map<number, CommunicationMessage>()
  for (const ai of aiMessages) {
    // Find the last inbound email before this AI message
    const preceding = emailMessages
      .filter((m) => m.direction === 'inbound' && parseUTCDate(m.created_at) <= parseUTCDate(ai.created_at))
      .pop()
    if (preceding) aiByInboundId.set(preceding.id, ai)
  }
  const analysisByMessageId = new Map(analysis.map((a) => [a.message_id, a]))

  // Each action item belongs to the email that raised it and, when the AI resolved it from an
  // email, to that email too; both show it as part of the message.
  const actorName = useActorName()
  const threadMessageIds = new Set(allMessages.map((m) => m.id))
  const { focusMessageId } = useActionEmail()
  const actionLinks = new Map<number, MessageActionLink[]>()
  const linkAction = (messageId: number | null | undefined, link: MessageActionLink) => {
    if (messageId == null || !threadMessageIds.has(messageId)) return
    actionLinks.set(messageId, [...(actionLinks.get(messageId) ?? []), link])
  }
  for (const action of [...actions].sort(
    (a, b) => parseUTCDate(a.created_at).getTime() - parseUTCDate(b.created_at).getTime(),
  )) {
    // Only a resolution or dismissal closes it; In progress (USA-1443) leaves it open.
    const closed =
      actionAuditEvents(action, actorName).find(
        (event) => event.kind === 'resolved' || event.kind === 'dismissed',
      ) ?? null
    linkAction(action.communication_message_id, { role: 'raised', action, closed })
    const resolvingId = actionResolvingMessageId(action)
    if (resolvingId !== action.communication_message_id) {
      linkAction(resolvingId, { role: 'resolved', action, closed })
    }
  }
  const entries = emailMessages.map((message) => ({ key: `message-${message.id}`, message }))

  // The newest real email: bounces after it sit folded below it and never take its place (USA-1704).
  const latestMessage =
    [...emailMessages].reverse().find((m) => !deliveryReport(m)) ?? emailMessages[emailMessages.length - 1]
  const realEnd = latestMessage ? emailMessages.indexOf(latestMessage) + 1 : entries.length
  const matchedIds = new Set(
    query ? emailMessages.filter((m) => messageMatches(m, query)).map((m) => m.id) : [],
  )
  // Messages someone has to act on, or that the AI is still working on, open by default.
  const needsEyes = (m: CommunicationMessage) =>
    m.status === 'pending_approval' ||
    (m.status === 'draft' && m.direction === 'outbound') ||
    (m.status === 'failed' && m.direction === 'outbound') ||
    analysisByMessageId.has(m.id)
  // Search results stay one line each, showing the words that matched, until clicked.
  // Those, and the emails that raised or resolved an action item, never fold away.
  const pinned = (m: CommunicationMessage) =>
    needsEyes(m) || actionLinks.has(m.id) || m.id === focusMessageId
  // A bounce stays one line, even as the latest email, until someone opens it (USA-1704).
  const defaultOpen = (m: CommunicationMessage) =>
    query
      ? false
      : m.id === focusMessageId || (!deliveryReport(m) && (m.id === latestMessage?.id || needsEyes(m)))
  const overrides = messageOverrides.query === query ? messageOverrides.open : new Map<number, boolean>()
  const isMessageOpen = (m: CommunicationMessage) => overrides.get(m.id) ?? defaultOpen(m)
  const toggleMessage = (m: CommunicationMessage) => {
    const next = new Map(overrides)
    next.set(m.id, !isMessageOpen(m))
    setMessageOverrides({ query, open: next })
  }

  // Long threads keep the opening message and the latest few; the middle folds into one row.
  // While searching, everything that does not match folds instead.
  const hidden = new Set<number>()
  if (query && !showAll) {
    entries.forEach((entry, i) => {
      if (!matchedIds.has(entry.message.id)) hidden.add(i)
    })
  } else if (!showAll && entries.length > FOLD_THRESHOLD) {
    for (let i = 1; i < entries.length - FOLD_KEEP_LATEST; i++) {
      const entry = entries[i]
      if (entry && !pinned(entry.message)) hidden.add(i)
    }
  }
  // Each run of hidden entries collapses to one row at its start, counting the messages inside.
  const foldRuns = new Map<number, number>()
  let runStart = -1
  entries.forEach((_entry, i) => {
    if (!hidden.has(i)) {
      runStart = -1
      return
    }
    if (runStart === -1) {
      runStart = i
      foldRuns.set(i, 0)
    }
    foldRuns.set(runStart, (foldRuns.get(runStart) ?? 0) + 1)
  })
  // Folding a single email saves nothing; show it instead.
  for (const [start, count] of foldRuns) {
    if (count === 1 && !query) {
      hidden.delete(start)
      foldRuns.delete(start)
    }
  }

  const latestPreview = latestMessage ? messagePreview(latestMessage) : null
  const counterpart = thread.recipient_name || thread.recipient_email

  const titleLine = (
    <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      {context?.badge}
      {invoice && (
        <Link
          to="/invoices/$invoiceId"
          params={{ invoiceId: invoice.uuid }}
          onClick={(event) => event.stopPropagation()}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border bg-muted/40 px-1.5 py-0.5 text-[11px] font-medium text-primary hover:underline"
        >
          <Receipt aria-hidden className="h-3 w-3" />
          Invoice {invoice.number}
        </Link>
      )}
      <span
        className={cn(
          'min-w-0 break-words text-sm font-semibold',
          collapsible && 'group-hover:underline group-focus-visible:underline',
          isClosed && !expanded ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {highlight(thread.subject, query)}
      </span>
      <ThreadAnalysisPill analyses={analysis} />
      {query && matchedIds.size > 0 && (
        <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:bg-amber-950/60 dark:text-amber-200">
          {matchedIds.size} {matchedIds.size === 1 ? 'match' : 'matches'}
        </span>
      )}
    </span>
  )

  const messageList = (start: number, end: number) =>
    start < end && (
      <ol className={cn('divide-y divide-border/70', start > 0 && 'border-t border-border/70')}>
        {entries.slice(start, end).map((entry, offset) => {
          const i = start + offset
          if (hidden.has(i)) {
            const runMessages = foldRuns.get(i)
            if (runMessages === undefined) return null
            const noun = query ? 'other' : 'earlier'
            return (
              <li key={`fold-${i}`}>
                <button
                  type="button"
                  onClick={() => setShowAllFor(query)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground sm:px-4"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center">
                    <ChevronsUpDown aria-hidden className="h-4 w-4" />
                  </span>
                  {`${runMessages} ${noun} message${runMessages === 1 ? '' : 's'}`}
                </button>
              </li>
            )
          }
          const message = entry.message
          return (
            <li key={entry.key} {...messageTargetProps(message.id)} className={ACTION_FOCUS_CLASS}>
              <ThreadMessage
                message={message}
                senderName={actorName(message.approved_by)}
                aiAnalysis={aiByInboundId.get(message.id)}
                analysis={analysisByMessageId.get(message.id)}
                actionLinks={actionLinks.get(message.id)}
                threadSubject={thread.subject}
                expanded={isMessageOpen(message)}
                onToggle={() => toggleMessage(message)}
                query={query}
                onApprove={onApprove}
                onReject={onReject}
                onSend={onSend}
                onUpdate={onUpdate}
                onRetry={onRetry}
                isRetrying={retryingUuid === message.uuid}
              />
            </li>
          )
        })}
      </ol>
    )

  return (
    <section
      data-testid={`communication-thread-${thread.uuid}`}
      aria-label={thread.subject}
      className={cn('group/thread min-w-0', isClosed && !expanded && 'text-muted-foreground')}
    >
      <div
        className={cn(
          'flex items-start gap-2 bg-card px-3 py-3 group-first/thread:rounded-t-lg sm:px-4',
          expanded ? 'border-b' : 'group-last/thread:rounded-b-lg',
        )}
      >
        {collapsible ? (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            title={expanded ? 'Collapse thread' : 'Expand thread'}
            className="group flex min-w-0 flex-1 items-start gap-2 text-left focus-visible:outline-none"
          >
            <ChevronRight
              aria-hidden
              className={cn(
                'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                expanded && 'rotate-90',
              )}
            />
            <span className="min-w-0 flex-1">
              {titleLine}
              {!expanded && (
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                  {counterpart}
                  {' · '}
                  {emailMessages.length} message{emailMessages.length === 1 ? '' : 's'}
                  {latestPreview?.text && <> · {latestPreview.text}</>}
                </span>
              )}
            </span>
          </button>
        ) : (
          <h4 className="min-w-0 flex-1">{titleLine}</h4>
        )}
        {!expanded && (
          <time
            dateTime={thread.last_activity_at}
            className="hidden shrink-0 text-xs text-muted-foreground tabular-nums sm:inline"
          >
            {formatEventTime(thread.last_activity_at)}
          </time>
        )}
      </div>

      {expanded && (
        <>
          <div
            className={cn('border-b bg-muted/30 px-3 py-2 sm:px-4', collapsible && 'sm:pl-10')}
          >
            <RecipientSummary
              to={
                // A text or call conversation (USA-217) has a name and no email address.
                thread.recipient_name && thread.recipient_email
                  ? `${thread.recipient_name} <${thread.recipient_email}>`
                  : thread.recipient_name || thread.recipient_email
              }
              ccEmails={ccList}
              onEdit={
                isClosed || isCustomerThread
                  ? undefined
                  : () => {
                      setEditRecipientsOpen(true)
                    }
              }
              editTitle="Edit thread recipients"
              actions={
                isClosed ? (
                  <LoadingButton
                    size="icon-xs"
                    variant="ghost"
                    loading={isReopening}
                    onClick={onReopen}
                    title="Reopen thread"
                    aria-label="Reopen thread"
                  >
                    <RotateCcw />
                  </LoadingButton>
                ) : (
                  <ActionPopover
                    align="end"
                    open={confirmClose}
                    onOpenChange={setConfirmClose}
                    trigger={
                      <Button size="icon-xs" variant="ghost" title="Close thread" aria-label="Close thread">
                        <CheckCircle2 />
                      </Button>
                    }
                    title="Close this thread? Its scheduled follow-up is cancelled."
                    confirmLabel="Close thread"
                    pending={isClosing}
                    pendingLabel="Closing..."
                    onConfirm={(_note, close) => onClose(close)}
                  />
                )
              }
            />
            {!isCustomerThread && (
              <EditRecipientsDialog
                thread={thread}
                open={editRecipientsOpen}
                onOpenChange={setEditRecipientsOpen}
              />
            )}
          </div>

          {messageList(0, realEnd)}

          {/* Below the latest real email, never over it: its AI-analysis bar runs along its bottom edge (USA-1536). */}
          <ThreadFollowUp
            threadUuid={thread.uuid ?? ''}
            status={thread.status}
            followUpAt={thread.follow_up_at ?? null}
            reason={thread.follow_up_reason}
            count={thread.follow_up_count}
            max={thread.max_follow_ups}
            checkIns={thread.upcoming_check_ins ?? []}
            className="pb-3 pl-14 pr-3 pt-1 sm:pl-[3.75rem] sm:pr-4"
          />

          {/* Bounces that came after it follow, one line each (USA-1704). */}
          {messageList(realEnd, entries.length)}

          <div className="border-t bg-muted/20 px-3 py-3 group-last/thread:rounded-b-lg sm:px-4">
            {recording ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Select value={recordSource} onValueChange={setRecordSource}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone_call">Phone Call</SelectItem>
                      <SelectItem value="in_person">In Person</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {recordSource === 'phone_call' && callTarget && (
                  <RecentCallPicker
                    target={callTarget}
                    onPickedChange={setCallPicked}
                    onLinked={() => {
                      setCallPicked(false)
                      setRecording(false)
                    }}
                  />
                )}
                {!callPicked && (
                <>
                <ComposeTextarea
                  value={recordText}
                  onChange={(e) => setRecordText(e.target.value)}
                  placeholder={context?.recordPlaceholder ?? 'Summarize what the manufacturer communicated...'}
                  rows={4}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <LoadingButton
                    size="sm"
                    loading={isRecording}
                    loadingText="Recording..."
                    onClick={() => {
                      onRecord(recordText, recordSource)
                      setRecordText('')
                      setRecording(false)
                    }}
                    disabled={!recordText.trim()}
                  >
                    <Phone className="mr-1 h-3 w-3" /> Record
                  </LoadingButton>
                  <Button size="sm" variant="outline" onClick={() => setRecording(false)} disabled={isRecording}>
                    Cancel
                  </Button>
                </div>
                </>
                )}
              </div>
            ) : composing ? (
              <div className="space-y-3">
                {editingReplyRecipients ? (
                  <div className="space-y-2 rounded-md border bg-muted/30 p-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <StaffEmailField
                        label="To"
                        value={draftComposeTo}
                        onChange={setDraftComposeTo}
                        staff={staffSuggestions}
                        placeholder={isCustomerThread ? 'customer@example.com' : 'vendor@example.com'}
                      />
                      <StaffEmailField
                        label="CC (optional)"
                        value={draftComposeCc}
                        onChange={setDraftComposeCc}
                        staff={staffSuggestions}
                        placeholder="another@example.com"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingReplyRecipients(false)}
                        disabled={isReplying}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setComposeTo(draftComposeTo)
                          setComposeCc(draftComposeCc)
                          setEditingReplyRecipients(false)
                        }}
                        disabled={!draftComposeTo.trim() || isReplying}
                      >
                        <Check className="mr-1 h-3 w-3" /> Done
                      </Button>
                    </div>
                  </div>
                ) : (
                  <RecipientSummary
                    to={composeTo}
                    ccEmails={splitEmails(composeCc)}
                    onEdit={() => {
                      setDraftComposeTo(composeTo)
                      setDraftComposeCc(composeCc)
                      setEditingReplyRecipients(true)
                    }}
                    editTitle="Edit reply recipients"
                  />
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-md border bg-muted/50 p-3 text-sm whitespace-pre-wrap text-muted-foreground">
                    {includeSignoff && greeting && <>{greeting}{`\n\n`}</>}
                    {composeText ? <MentionText content={composeText} /> : '...'}
                    {includeSignoff && (
                      <>{`\n\n`}{signoff}</>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <MentionInput
                      key={composeSeed?.id}
                      initialValue={composeSeed?.text}
                      ref={composeInputRef}
                      onChange={setComposeText}
                      onMentionSelect={(result: MentionResult) => {
                        if (result.entity_type !== 'staff' || !result.email) return
                        const toEmails = splitEmails(composeTo)
                        const ccEmails = splitEmails(composeCc)
                        const emailKey = result.email.toLowerCase()
                        if (
                          !toEmails.some((email) => email.toLowerCase() === emailKey) &&
                          !ccEmails.some((email) => email.toLowerCase() === emailKey)
                        ) {
                          const nextCc = [...ccEmails, result.email].join(', ')
                          setComposeCc(nextCc)
                          if (editingReplyRecipients) setDraftComposeCc(nextCc)
                        }
                      }}
                      placeholder="Type your message... Use @ to mention staff"
                      className="min-h-[136px] flex-1 text-sm"
                      disabled={isReplying}
                    />
                    <div className="flex flex-col gap-1">
                      {offersPersonal && currentUser ? (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                          <span>Sign as</span>
                          <ReviewChoiceGroup
                            label="Sign as"
                            value={signAs}
                            options={[
                              { value: 'team', label: 'Team' },
                              {
                                value: 'personal',
                                label: currentUser.name?.trim().split(/\s+/)[0] || currentUser.email,
                              },
                              { value: 'none', label: 'None' },
                            ]}
                            onChange={(next) => {
                              setSignAs(next)
                              rememberSignAs(next)
                            }}
                            disabled={isReplying}
                          />
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={includeSignoff}
                            onChange={(e) => setSignAs(e.target.checked ? 'team' : 'none')}
                            disabled={isReplying}
                          />
                          Include greeting &amp; sign-off
                        </label>
                      )}
                      {/* PO, quote and shipment documents attach only to the PO's own threads. */}
                      {poDocuments && (
                        <>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={attachPoPdf}
                              onChange={(e) => setAttachPoPdf(e.target.checked)}
                              disabled={!hasPoPdf || isReplying}
                            />
                            Attach PO PDF{' '}
                            {!hasPoPdf && (
                              <span className="text-muted-foreground">(not available)</span>
                            )}
                          </label>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={attachQuotePdf}
                              onChange={(e) => setAttachQuotePdf(e.target.checked)}
                              disabled={!hasQuotePdf || isReplying}
                            />
                            Attach Quote PDF{' '}
                            {!hasQuotePdf && (
                              <span className="text-muted-foreground">(not available)</span>
                            )}
                          </label>
                          {shipmentDocOptions.map((opt) => {
                            const key = `${opt.shipmentId}:${opt.docType}`
                            return (
                              <label key={key} className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={selectedShipmentDocs.has(key)}
                                  onChange={(e) => {
                                    setSelectedShipmentDocs((prev) => {
                                      const next = new Set(prev)
                                      if (e.target.checked) next.add(key)
                                      else next.delete(key)
                                      return next
                                    })
                                  }}
                                  disabled={isReplying}
                                />
                                Attach {opt.docType === 'bol' ? 'BOL' : 'Label'}{' '}
                                <span className="text-muted-foreground">
                                  ({opt.shipmentLabel} — {opt.filename})
                                </span>
                              </label>
                            )
                          })}
                        </>
                      )}
                    </div>
                    <ReplyAttachments
                      attachments={composeAttachments}
                      onChange={setComposeAttachments}
                      attaching={attaching}
                      onAttachingChange={setAttaching}
                      disabled={isReplying}
                    />
                    {sendError && (
                      <div
                        role="alert"
                        className="flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm"
                      >
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                        <div>
                          <p className="font-medium text-foreground">Email not sent</p>
                          <p className="mt-1 text-foreground">{sendError}</p>
                          <p className="mt-1 text-xs text-foreground/80">
                            Your draft is still here. Review the message and try again.
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <LoadingButton
                        size="sm"
                        loading={isReplying}
                        loadingText="Sending..."
                        onClick={() => {
                          const plainComposeText = mentionContentToPlainText(composeText)
                          const fullBody = includeSignoff
                            ? `${greeting ? `${greeting}\n\n` : ''}${plainComposeText}\n\n${signoff}`
                            : plainComposeText
                          const toEmails = splitEmails(composeTo)
                          const ccEmails = splitEmails(composeCc)
                          const invalidEmail = [...toEmails, ...ccEmails].find(
                            (email) => !email.includes('@'),
                          )
                          if (toEmails.length === 0) {
                            setSendError('Add at least one To recipient.')
                            return
                          }
                          if (invalidEmail) {
                            setSendError(`Enter a valid email address for ${invalidEmail}.`)
                            return
                          }
                          const shipmentDocRefs = Array.from(selectedShipmentDocs).map((key) => {
                            const [idStr, docType] = key.split(':')
                            return {
                              shipment_id: Number(idStr),
                              doc_type: docType as 'bol' | 'label',
                            }
                          })
                          setSendError(null)
                          onReply(
                            fullBody,
                            {
                              attachPoPdf,
                              attachQuotePdf,
                              toEmails,
                              ccEmails,
                              attachShipmentDocs: shipmentDocRefs,
                              attachments: composeAttachments,
                            },
                            {
                              onSuccess: () => {
                                setComposeText('')
                                composeInputRef.current?.clear()
                                setComposeTo(thread.recipient_email ?? '')
                                setComposeCc(parseCcString(thread.cc_emails))
                                setEditingReplyRecipients(false)
                                setAttachPoPdf(false)
                                setAttachQuotePdf(false)
                                setSelectedShipmentDocs(new Set())
                                setSignAs(defaultSignAs())
                                setComposeAttachments([])
                                setSendError(null)
                                setComposeSeed(null)
                                setComposing(false)
                              },
                              onError: setSendError,
                            },
                          )
                        }}
                        disabled={!composeText.trim() || !composeTo.trim() || attaching}
                      >
                        <Send className="mr-1 h-3 w-3" /> Send
                      </LoadingButton>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setComposing(false)
                          setComposeSeed(null)
                          setComposeTo(thread.recipient_email ?? '')
                          setComposeCc(parseCcString(thread.cc_emails))
                          setEditingReplyRecipients(false)
                          setAttachPoPdf(false)
                          setAttachQuotePdf(false)
                          setSelectedShipmentDocs(new Set())
                          setSignAs(defaultSignAs())
                          setComposeAttachments([])
                          setSendError(null)
                        }}
                        disabled={isReplying || attaching}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-between">
                {(invoice && !context?.onRecord) || context?.canRecord === false ? (
                  <span />
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setRecording(true)}>
                    <Phone className="mr-1 h-3 w-3" /> Record Communication
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setComposeTo(thread.recipient_email ?? '')
                    setComposeCc(parseCcString(thread.cc_emails))
                    setEditingReplyRecipients(false)
                    setComposing(true)
                  }}
                >
                  <MessageSquare className="mr-1 h-3 w-3" /> Reply
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  )
}

export function ThreadViewWithReply({
  thread,
  invoice,
  context,
  actions = [],
  open,
  onOpenChange,
  collapsible = true,
  query = '',
  poUuid,
  hasPoPdf,
  hasQuotePdf,
  shipmentDocOptions,
  onApprove,
  onReject,
  onSend,
  onUpdate,
}: {
  thread: ThreadWithSchedule
  /** Set for a thread on one of the PO's invoices: links the invoice, drops PO-only reply tools. */
  invoice?: ThreadInvoice
  /** A Quote Builder conversation's greeting, sign-off, recording and badge (USA-1094). */
  context?: ThreadContextOptions
  actions?: ActionItemLike[]
  /** Controlled open state; without it the thread opens unless closed. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** False when the thread is shown on its own (picked from a list) and always open. */
  collapsible?: boolean
  /** Normalized search text (see `normalizeQuery`); matching messages open and highlight. */
  query?: string
  /** The PO a recorded call is saved on; quote threads record through ``context.onRecord``. */
  poUuid?: string
  hasPoPdf: boolean
  hasQuotePdf: boolean
  shipmentDocOptions: ShipmentDocOption[]
  onApprove: (uuid: string) => void
  onReject: (uuid: string) => void
  onSend: (uuid: string) => void
  onUpdate: (uuid: string, body: MessageDraftUpdate) => void
}) {
  const replyMutation = useSendReply(thread.uuid ?? '')
  const recordMutation = useRecordCommunication(poUuid ?? '')
  const closeMutation = useCloseThread()
  const reopenMutation = useReopenThread()
  const retryMutation = useRetryMessage()
  return (
    <ThreadView
      thread={thread}
      invoice={invoice}
      context={context}
      analysis={thread.analysis ?? []}
      open={open}
      onOpenChange={onOpenChange}
      collapsible={collapsible}
      query={query}
      actions={actions}
      hasPoPdf={hasPoPdf}
      hasQuotePdf={hasQuotePdf}
      shipmentDocOptions={shipmentDocOptions}
      onApprove={onApprove}
      onReject={onReject}
      onSend={onSend}
      onUpdate={onUpdate}
      onRetry={(uuid) =>
        retryMutation.mutate(uuid, {
          onSuccess: () => toast.success('Email resent'),
          onError: (err) => toast.error(formatApiErrorMessage('Retry failed', err)),
        })
      }
      retryingUuid={retryMutation.isPending ? (retryMutation.variables ?? null) : null}
      onReply={(bodyText, opts, callbacks) =>
        replyMutation.mutate(
          {
            body_text: bodyText,
            to_emails: opts.toEmails,
            cc_emails: opts.ccEmails,
            attach_po_pdf: opts.attachPoPdf,
            attach_quote_pdf: opts.attachQuotePdf,
            attach_shipment_docs: opts.attachShipmentDocs,
            attachments: opts.attachments.map((a) => ({
              filename: a.filename,
              content_type: a.contentType,
              content_base64: a.contentBase64,
            })),
          },
          {
            onSuccess: () => {
              toast.success('Email sent')
              callbacks.onSuccess()
            },
            onError: (err) => {
              callbacks.onError(getApiErrorMessage(err, 'Check your connection and try again.'))
            },
          },
        )
      }
      isReplying={replyMutation.isPending}
      onRecord={context?.onRecord ?? ((bodyText, source) => recordMutation.mutate({ body_text: bodyText, source }))}
      isRecording={context?.onRecord ? Boolean(context.isRecording) : recordMutation.isPending}
      onClose={(onDone) =>
        closeMutation.mutate(thread.uuid ?? '', {
          onSuccess: () => {
            toast.success('Thread closed')
            onDone()
          },
          onError: (err) => toast.error(formatApiErrorMessage('Close failed', err)),
        })
      }
      onReopen={() =>
        reopenMutation.mutate(
          { threadUuid: thread.uuid ?? '' },
          {
            onSuccess: () => toast.success('Thread reopened'),
            onError: (err) => toast.error(formatApiErrorMessage('Reopen failed', err)),
          },
        )
      }
      isClosing={closeMutation.isPending}
      isReopening={reopenMutation.isPending}
    />
  )
}
